import { Object as FabricObject } from 'fabric/fabric-impl';
import type { FabricEditor } from 'modules/fabric-editor';
import { isPresetTemplate } from 'modules/manager/domain/template-access';
import { saveTemplate, selectActiveTemplate, selectIsSaving } from 'modules/manager/store';
import { useCallback } from 'react';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { useEditorDirty } from './use-editor-dirty';

export interface UseSaveTemplateResult {
  save: () => Promise<void>;
  isDirty: boolean;
  isSaving: boolean;
  canSave: boolean;
  /** False when a regular user opens a global preset — they must duplicate it first. */
  canEdit: boolean;
}

export function useSaveTemplate(editor: FabricEditor | null): UseSaveTemplateResult {
  const dispatch = useAppDispatch();
  const template = useAppSelector(selectActiveTemplate);
  const isSaving = useAppSelector(selectIsSaving);
  const isDirty = useEditorDirty(editor);
  const isAdmin = useIsAdmin();

  // A global preset is editable only by admins; everyone else duplicates it first.
  const canEdit = template ? !isPresetTemplate(template.access) || isAdmin : false;

  const save = useCallback(async () => {
    if (!editor || !template) return;
    if (!canEdit) return;
    if (!editor.dirty?.isDirty()) return;
    if (isSaving) return;

    const json = editor.canvas.toJSON(['data']) as { version: string; objects: FabricObject[] };
    const result = await dispatch(saveTemplate({ id: template.id, objects: json.objects ?? [], version: json.version }));
    if (saveTemplate.fulfilled.match(result)) {
      editor.dirty?.markClean();
    }
  }, [editor, template, isSaving, canEdit, dispatch]);

  return {
    save,
    isDirty,
    isSaving,
    canSave: canEdit && isDirty && !isSaving && editor !== null && template !== null,
    canEdit,
  };
}
